package com.evoapps.evoshift.domain.availability

import com.evoapps.evoshift.domain.model.Availability
import com.evoapps.evoshift.domain.model.AvailabilityDay
import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.DayPreference
import com.evoapps.evoshift.domain.model.HoursRange
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.SharedAvailability

/**
 * SH-135: a person's hours (one availability for all their workplaces) and each workplace's
 * `definedAvailabilitySlots` (what its managers know) in both directions.
 *
 * Hours to slots, per day:
 * - unavailable stays unavailable;
 * - hours that miss every slot of the workplace: unavailable there (they cannot work any of them);
 * - the preferred slot is the one most covered by the preferred hours, or, without those, by the
 *   available hours when they leave some slot partly or wholly out; none when every slot fits;
 * - the comment is the shared one plus the one for this workplace only.
 *
 * Slots to hours (forms and documents from before SH-135): the preferred slot becomes the
 * preferred hours (none for an all-day slot) and the comment stays with that workplace only, so
 * nothing written to one employer reaches another.
 */
object AvailabilitySlotMapping {

    private const val DAY_MINUTES = 24 * 60

    /** One day of [day] as workplace [workplaceId] sees it, on its [slots]. */
    fun toWorkplaceDay(day: AvailabilityDay, slots: List<AvailabilitySlot>, workplaceId: String): DayPreference {
        val comment = listOfNotNull(
            day.comment?.trim()?.takeIf { it.isNotEmpty() },
            day.workplaceComments[workplaceId]?.trim()?.takeIf { it.isNotEmpty() }
        ).joinToString("\n").ifEmpty { null }
        val hours = day.ranges.filter { it.minutes != null }
        if (day.status == AvailabilityStatus.UNAVAILABLE) {
            return DayPreference(AvailabilityStatus.UNAVAILABLE, null, comment)
        }
        val available = hours.mapNotNull { it.minutes }
        val slotMinutes = slots.mapNotNull { slot -> slotMinutes(slot)?.let { slot to it } }
        if (available.isNotEmpty() && slotMinutes.isNotEmpty() && slotMinutes.none { (_, m) -> covered(m, available) > 0 }) {
            return DayPreference(AvailabilityStatus.UNAVAILABLE, null, comment, hours)
        }
        val preferred = day.preferred.mapNotNull { it.minutes }
        val slotId = when {
            preferred.isNotEmpty() -> bestSlot(slotMinutes, preferred)
            available.isNotEmpty() && slotMinutes.any { (_, m) -> covered(m, available) < m.count() } -> bestSlot(slotMinutes, available)
            else -> null
        }
        return DayPreference(AvailabilityStatus.AVAILABLE, slotId, comment, hours)
    }

    /** A workplace's [pref] (on its [slots]) as hours; its comment stays with [workplaceId]. */
    fun fromWorkplaceDay(pref: DayPreference, slots: List<AvailabilitySlot>, workplaceId: String): AvailabilityDay {
        val slot = slots.firstOrNull { it.id == pref.preferredSlotId }
        val preferred = slot?.takeIf { s -> slotMinutes(s)?.let { it.first == 0 && it.last == DAY_MINUTES - 1 } != true }
            ?.let { listOf(HoursRange(it.startTime, it.endTime)) }.orEmpty()
        val comment = pref.comment?.trim()?.takeIf { it.isNotEmpty() }
        return AvailabilityDay(
            status = pref.status,
            ranges = pref.hours,
            preferred = if (pref.status == AvailabilityStatus.AVAILABLE) preferred else emptyList(),
            comment = null,
            workplaceComments = comment?.let { mapOf(workplaceId to it) }.orEmpty()
        )
    }

    /** The copy of [shared] workplace [workplaceId] reads, on its [slots] (`availability` collection). */
    fun toWorkplace(shared: SharedAvailability, workplaceId: String, slots: List<AvailabilitySlot>, isLocked: Boolean = false): Availability {
        val (year, month) = shared.month.take(4).toInt() to shared.month.substring(5, 7).toInt()
        return Availability(
            userId = shared.userId,
            rosterId = RosterIds.of(workplaceId, year, month),
            workplaceId = workplaceId,
            month = shared.month,
            isLocked = isLocked,
            isFilled = shared.isFilled,
            days = shared.days.mapValues { (_, day) -> toWorkplaceDay(day, slots, workplaceId) }
        )
    }

    /**
     * Documents written per workplace before SH-135 ([legacy], each with its workplace's slots) as
     * one month. A day two workplaces disagree on is available when either says so (hours and
     * preferences of both kept); comments stay with their workplace. [ownerOf] gives each
     * workplace's owner: those owners count as already sent to.
     */
    fun merge(
        userId: String,
        month: String,
        legacy: List<Pair<Availability, List<AvailabilitySlot>>>,
        ownerOf: (String) -> String?
    ): SharedAvailability {
        val days = mutableMapOf<Int, AvailabilityDay>()
        legacy.forEach { (doc, slots) ->
            val wp = doc.workplaceId ?: RosterIds.workplaceIdOf(doc.rosterId.orEmpty())
            doc.days.forEach { (n, pref) ->
                val day = fromWorkplaceDay(pref, slots, wp)
                days[n] = days[n]?.let { combine(it, day) } ?: day
            }
        }
        return SharedAvailability(
            userId = userId,
            month = month,
            days = days,
            isFilled = legacy.any { it.first.isFilled },
            releasedToOwnerIds = legacy.mapNotNull { (doc, _) -> (doc.workplaceId ?: RosterIds.workplaceIdOf(doc.rosterId.orEmpty())).let(ownerOf) }.toSet()
        )
    }

    private fun combine(a: AvailabilityDay, b: AvailabilityDay): AvailabilityDay {
        val status = if (a.status == AvailabilityStatus.AVAILABLE || b.status == AvailabilityStatus.AVAILABLE) AvailabilityStatus.AVAILABLE else AvailabilityStatus.UNAVAILABLE
        fun ofAvailable(d: AvailabilityDay, pick: (AvailabilityDay) -> List<HoursRange>) = if (d.status == AvailabilityStatus.AVAILABLE) pick(d) else emptyList()
        // An available day without hours is the whole day: it wins over the other's hours.
        val wholeDay = listOf(a, b).any { it.status == AvailabilityStatus.AVAILABLE && it.ranges.isEmpty() }
        return AvailabilityDay(
            status = status,
            ranges = if (wholeDay) emptyList() else (ofAvailable(a) { it.ranges } + ofAvailable(b) { it.ranges }).distinct(),
            preferred = (ofAvailable(a) { it.preferred } + ofAvailable(b) { it.preferred }).distinct(),
            comment = listOfNotNull(a.comment, b.comment).distinct().joinToString("\n").ifEmpty { null },
            workplaceComments = a.workplaceComments + b.workplaceComments
        )
    }

    /** Minutes of [slot]; an end at or before the start runs to midnight. */
    private fun slotMinutes(slot: AvailabilitySlot): IntRange? {
        val from = HoursRange.minutesOf(slot.startTime) ?: return null
        val to = HoursRange.minutesOf(slot.endTime)?.let { if (it <= from) DAY_MINUTES else it } ?: return null
        return from until to
    }

    private fun covered(slot: IntRange, ranges: List<IntRange>): Int {
        if (slot.isEmpty()) return 0
        // Merge first, so overlapping ranges do not count twice.
        var total = 0
        var reach = slot.first
        ranges.sortedBy { it.first }.forEach { r ->
            val from = maxOf(r.first, reach, slot.first)
            val to = minOf(r.last, slot.last)
            if (to >= from) {
                total += to - from + 1
                reach = to + 1
            }
        }
        return total
    }

    /** The slot with the largest share covered by [ranges]; the earlier one on a tie; none if none is touched. */
    private fun bestSlot(slots: List<Pair<AvailabilitySlot, IntRange>>, ranges: List<IntRange>): String? =
        slots.map { (slot, m) -> slot to covered(m, ranges).toDouble() / m.count() }
            .filter { it.second > 0.0 }
            .maxWithOrNull(compareBy<Pair<AvailabilitySlot, Double>> { it.second }.thenByDescending { slotMinutes(it.first)?.first ?: 0 })
            ?.first?.id
}
